import { useState } from 'react'
import type { MarginNoteBlock, PinSide } from '../../lib/types'
import { AsideAction, AsideActions, NoteText, SourceForm, SourceNote, type SelectableProps, type SourceProps } from './ThoughtParts'

/** Pinning, for a note that is pinned (or being pinned). */
export interface PinControls {
  /** Paragraphs it covers now. */
  count: number
  /** Paragraphs it could cover, its own first (to the next pinned note, scene, divider or the end). */
  max: number
  onSpan: (span: number) => void
  /** The margin it sits in (left unless chosen). */
  side: PinSide
  onSide: (side: PinSide) => void
  /** An ordinary note again, back in the right margin. */
  onUnpin: () => void
}

/** The paragraph count's field, which the composer focuses when a note is pinned. */
export const SPAN_INPUT_LABEL = 'Number of paragraphs pinned'

/**
 * A margin note (design 4a): beside the paragraph above it, a Burrow card
 * with the small MARGIN NOTE label, italic words and where they came from.
 * It lives in the right margin at the wide breakpoint and inline under its
 * paragraph below it (see .wr-aside-right). One made from selected words
 * names them, and while it's still empty offers your thoughts instead.
 *
 * Pinned (Niki & Andrew, 10/2: pins and margin notes are one thing, a note
 * pinned across paragraphs), it's the old pin card: in the left margin (or
 * the right, as chosen), sticky beside the paragraphs it covers, with the
 * paragraph count to type or step. The composer wraps the covered blocks in
 * a .wr-anchor with a .wr-aside-left-track (or -right-track) holding it;
 * below the wide breakpoint it sits inline above them. The pin button pins
 * it at once, across its own paragraph, and the composer opens the options
 * with the count focused, ready to type (Niki, 10/2: no choosing by
 * pointing).
 */
export function MarginNoteView({
  block,
  selected,
  sourceFormOpen,
  onSelect,
  onKeyDown,
  onRemove,
  onText,
  onLinkKey,
  onIntoColumn,
  onFromThoughts,
  onEditSource,
  onCloseSource,
  onSaveSource,
  pin,
  onPin,
}: SelectableProps &
  SourceProps & {
    block: MarginNoteBlock
    /** The words changed: their text, and the links-only html when some are linked. */
    onText: (text: string, html: string | undefined) => void
    /** ⌘K / Ctrl+K in the words (NoteText). */
    onLinkKey?: () => boolean
    onIntoColumn: () => void
    /** Fill it from one of your thoughts instead of typing. */
    onFromThoughts?: () => void
    /** Set when it's pinned. */
    pin?: PinControls
    /** The pin button: pin it (or, pinned, open its options) with the count ready to type. Absent: nothing to pin it beside. */
    onPin?: () => void
  }) {
  const hasSource = !!(block.source?.title || block.source?.url)
  const pinned = !!pin
  const fromThoughts = onFromThoughts && block.own && !block.text.trim() && (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation()
        onFromThoughts()
      }}
      className="self-start font-sans text-[11px] font-semibold text-primary hover:underline"
    >
      Or add one of your thoughts
    </button>
  )
  const source = (
    <SourceNote
      block={block}
      onAdd={() => {
        onSelect()
        onEditSource()
      }}
      className="text-muted-foreground"
    />
  )
  const step = 'flex size-6 items-center justify-center text-[14px] leading-none text-muted-foreground hover:text-primary disabled:opacity-30'
  const sideBtn = (on: boolean) =>
    `rounded-full px-2 py-0.5 leading-none transition-colors ${on ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`
  const actions = selected && (
    <AsideActions label={pinned ? 'Pinned note options' : 'Margin note options'}>
      {pin && (
        <span role="group" aria-label="Paragraphs pinned" className="flex items-center rounded-full border border-border/80 px-0.5">
          <button type="button" aria-label="Pin fewer paragraphs" disabled={pin.count <= 1} onClick={() => pin.onSpan(pin.count - 1)} className={step}>
            −
          </button>
          <SpanInput count={pin.count} max={pin.max} onSpan={pin.onSpan} />
          <button type="button" aria-label="Pin more paragraphs" disabled={pin.count >= pin.max} onClick={() => pin.onSpan(pin.count + 1)} className={step}>
            +
          </button>
        </span>
      )}
      {pin && (
        // Which margin it sits in (Niki, 10/2). Where the margins fold inline (narrow windows, phones) it sits above its paragraphs either way.
        <span role="group" aria-label="Margin" className="flex items-center rounded-full border border-border/80 p-0.5">
          {(['left', 'right'] as const).map((side) => (
            <button
              key={side}
              type="button"
              aria-pressed={pin.side === side}
              title={side === 'left' ? 'In the left margin' : 'In the right margin'}
              onClick={(e) => {
                e.stopPropagation()
                if (pin.side !== side) pin.onSide(side)
              }}
              className={sideBtn(pin.side === side)}
            >
              {side === 'left' ? 'Left' : 'Right'}
            </button>
          ))}
        </span>
      )}
      {onPin && !pinned && <AsideAction onClick={onPin}>Pin to paragraphs</AsideAction>}
      {pin && <AsideAction onClick={pin.onUnpin}>Unpin</AsideAction>}
      <AsideAction onClick={onIntoColumn}>Into the column</AsideAction>
      {!block.own && <AsideAction onClick={onEditSource}>{hasSource ? 'Edit source' : 'Add source'}</AsideAction>}
      <AsideAction danger onClick={onRemove}>
        Remove
      </AsideAction>
    </AsideActions>
  )
  const pinButton = onPin && <PinButton pinned={pinned} onClick={onPin} />
  const words = block.words ? ` · ON “${block.words.toUpperCase()}”` : ''

  if (pinned) {
    const shown = pin.count
    return (
      <div
        data-selectable={block.id}
        tabIndex={0}
        role="group"
        aria-label="Pinned margin note"
        onClick={(e) => {
          e.stopPropagation()
          onSelect()
        }}
        onKeyDown={onKeyDown}
        className="flex flex-col gap-2 outline-none"
      >
        <span className="flex items-center gap-1.5 font-sans text-[9.5px] font-semibold tracking-[0.08em] text-muted-foreground">
          <span aria-hidden className="size-[7px] shrink-0 rounded-full bg-primary" />
          <span className="min-w-0 flex-1 truncate" title={block.words ? `On “${block.words}”` : undefined}>
            PINNED · {shown} PARAGRAPH{shown === 1 ? '' : 'S'}
            {words}
          </span>
          {pinButton}
        </span>
        <div
          className={`flex flex-col gap-2 rounded-2xl border border-card-edge bg-card px-4 pb-3.5 pt-4 transition-shadow ${selected ? 'ring-2 ring-accent/40' : ''}`}
        >
          <NoteText block={block} onChange={onText} onLinkKey={onLinkKey} className="font-display text-[15px]/[1.5] text-foreground/85" />
          {fromThoughts}
          {source}
        </div>
        <span className="font-sans text-[11px] font-medium text-muted-foreground">Stays beside the text as it scrolls</span>
        {actions}
        {sourceFormOpen && <SourceForm initial={block.source} own={!!block.own} onCancel={onCloseSource} onSave={onSaveSource} />}
      </div>
    )
  }

  return (
    <div
      data-selectable={block.id}
      tabIndex={0}
      role="group"
      aria-label="Margin note"
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={onKeyDown}
      className={`group/note relative flex flex-col gap-2 rounded-2xl border border-card-edge bg-card px-4 pb-3.5 pt-4 outline-none transition-shadow ${selected ? 'ring-2 ring-accent/40' : ''}`}
    >
      <span className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate font-sans text-[9.5px] font-semibold tracking-[0.08em] text-muted-foreground">
          MARGIN NOTE
          {words}
        </span>
        {pinButton}
      </span>
      <NoteText block={block} onChange={onText} onLinkKey={onLinkKey} className="font-serif text-[14.5px]/[1.45] italic text-foreground" />
      {fromThoughts}
      {source}
      {actions}
      {sourceFormOpen && <SourceForm initial={block.source} own={!!block.own} onCancel={onCloseSource} onSave={onSaveSource} />}
    </div>
  )
}

/**
 * The pin on a margin note: pin it across paragraphs (or, pinned, open its
 * options). Quiet, but always there on the card, so it's plain that a note
 * can span paragraphs.
 */
function PinButton({ pinned, onClick }: { pinned: boolean; onClick: () => void }) {
  const label = pinned ? 'Pin options' : 'Pin to paragraphs'
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={`-my-1 -mr-1.5 flex size-6 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-foreground/6 hover:text-primary ${
        pinned ? 'text-primary' : 'text-muted-foreground/70'
      }`}
    >
      <PinIcon filled={pinned} />
    </button>
  )
}

export function PinIcon({ filled = false, size = 13 }: { filled?: boolean; size?: number }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6l-1 6 4 4H6l4-4-1-6Z" />
      <path d="M12 13v8" />
    </svg>
  )
}

/**
 * How many paragraphs the pin covers, typed (Niki, 10/1) as well as stepped.
 * A number in range takes as it's typed; anything else settles back on blur
 * (clamped to 1..max), and Enter or Escape leaves the field.
 */
function SpanInput({ count, max, onSpan }: { count: number; max: number; onSpan: (span: number) => void }) {
  const [draft, setDraft] = useState(String(count))
  const [editing, setEditing] = useState(false)
  const shown = editing ? draft : String(count)
  const settle = () => {
    setEditing(false)
    const n = parseInt(draft, 10)
    if (Number.isFinite(n)) {
      const clamped = Math.min(Math.max(n, 1), Math.max(max, 1))
      if (clamped !== count) onSpan(clamped)
    }
  }
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label={SPAN_INPUT_LABEL}
      value={shown}
      // The toolbar holds the page's selection on mousedown; this field takes focus.
      onMouseDown={(e) => e.stopPropagation()}
      onFocus={(e) => {
        setDraft(String(count))
        setEditing(true)
        e.currentTarget.select()
      }}
      onChange={(e) => {
        const v = e.target.value.replace(/\D/g, '').slice(0, 3)
        setDraft(v)
        const n = parseInt(v, 10)
        if (n >= 1 && n <= max && n !== count) onSpan(n)
      }}
      onBlur={settle}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Typing here must never reach the note's own keys (Backspace removes it).
        e.stopPropagation()
        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur()
        if (e.key === 'ArrowUp' && count < max) {
          e.preventDefault()
          onSpan(count + 1)
          setDraft(String(count + 1))
        }
        if (e.key === 'ArrowDown' && count > 1) {
          e.preventDefault()
          onSpan(count - 1)
          setDraft(String(count - 1))
        }
      }}
      className="w-7 rounded-[4px] bg-transparent text-center tabular-nums text-foreground outline-none focus:bg-secondary"
    />
  )
}
